import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Store, DollarSign, ShieldCheck, Rocket, ArrowRight,
  ChevronDown, Sparkles, Package, Star,
} from "lucide-react";
import type { RootState } from "../../redux/store";

/* ─────────────────────────────────────────────────────────
   HEARTLAS DESIGN TOKENS — same palette as SmartFooter/AtlasHome/HostingPage
───────────────────────────────────────────────────────── */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  .sp-root *, .sp-root *::before, .sp-root *::after { box-sizing: border-box; }
  .sp-root { font-family:'DM Sans',sans-serif; background:${HEART.bg}; color:${HEART.dark}; }

  @keyframes spFloat { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
  @keyframes spPop { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

  .sp-hero-emoji{ animation:spFloat 3.2s ease-in-out infinite; display:inline-block; }

  .sp-cta{
    display:inline-flex;align-items:center;gap:10px;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;border:none;border-radius:14px;
    padding:16px 30px;font-family:'Sora',sans-serif;font-size:16px;font-weight:800;
    cursor:pointer;transition:transform .18s, box-shadow .2s;
    box-shadow:0 10px 24px rgba(200,32,46,.32);
  }
  .sp-cta:hover{ transform:translateY(-2px); box-shadow:0 14px 30px rgba(200,32,46,.4); }

  .sp-perk-card{
    background:${HEART.panelBg}; border:1.5px solid ${HEART.border}; border-radius:18px;
    padding:22px 20px; display:flex; flex-direction:column; gap:10px;
    animation:spPop .35s ease both; transition:transform .15s, box-shadow .15s, border-color .15s;
  }
  .sp-perk-card:hover{ transform:translateY(-3px); box-shadow:0 12px 26px rgba(27,14,6,0.08); border-color:${HEART.gold}; }

  .sp-perk-icon{
    width:46px;height:46px;border-radius:50%;
    background:${HEART.goldSoft}; color:${HEART.red};
    display:flex;align-items:center;justify-content:center;flex-shrink:0;
  }

  .sp-cat-pill{
    display:inline-flex;align-items:center;gap:6px;
    background:${HEART.goldSoft}; color:${HEART.redDark};
    border:1px solid ${HEART.border}; border-radius:999px;
    padding:8px 14px; font-size:12.5px; font-weight:700;
  }

  .sp-faq-item{
    border:1.5px solid ${HEART.border}; border-radius:14px; overflow:hidden;
    background:${HEART.panelBg};
  }
  .sp-faq-q{
    width:100%; display:flex; align-items:center; justify-content:space-between; gap:12px;
    background:transparent; border:none; cursor:pointer; text-align:left;
    padding:16px 18px; font-family:'Sora',sans-serif; font-weight:700; font-size:14.5px; color:${HEART.dark};
  }
  .sp-faq-chevron{ transition:transform .2s; flex-shrink:0; color:${HEART.red}; }
  .sp-faq-chevron.open{ transform:rotate(180deg); }
  .sp-faq-a{
    padding:0 18px; max-height:0; overflow:hidden; transition:max-height .25s ease, padding .25s ease;
    font-size:13.5px; color:${HEART.textMuted}; line-height:1.6;
  }
  .sp-faq-a.open{ padding:0 18px 18px; max-height:300px; }

  .sp-footlink{ color:${HEART.textMuted}; text-decoration:none; font-size:13px; font-weight:500; cursor:pointer; }
  .sp-footlink:hover{ color:${HEART.red}; }

  @media(max-width:760px){
    .sp-wrap{ padding:32px 18px 56px !important; }
    .sp-hero-title{ font-size:26px !important; }
    .sp-perk-grid{ grid-template-columns:1fr !important; }
  }
`;

/* ─────────────────────────────────────────────────────────
   CATEGORY PILLS — "sell anything" reassurance
───────────────────────────────────────────────────────── */
const CATEGORIES = [
  "Fashion", "Electronics", "Beauty", "Groceries", "Furniture",
  "Baby & Kids", "Books", "Automotive", "Farm goods", "and more",
];

/* ─────────────────────────────────────────────────────────
   FAQ DATA
───────────────────────────────────────────────────────── */
const FAQS: { q: string; a: string }[] = [
  {
    q: "What can I sell on Heartlas?",
    a: "Pretty much anything — fashion, electronics, beauty, groceries, furniture, books, and more. If it's legal to sell, there's a home for it on Heartlas.",
  },
  {
    q: "How much does it cost to start selling?",
    a: "Opening your store is free. Heartlas only takes a small commission when you actually make a sale — no upfront fees, no monthly subscription required.",
  },
  {
    q: "How do I get paid?",
    a: "Payouts are sent automatically to your linked bank account after each order is completed, so you always know where your money is.",
  },
  {
    q: "Can I manage my own prices and stock?",
    a: "Yes. You're fully in control — set your own prices, update inventory anytime, and run promotions whenever you like from your seller dashboard.",
  },
  {
    q: "Is there support if something goes wrong?",
    a: "Our support team and buyer/seller protections are there to help resolve disputes, so you can focus on running your store.",
  },
];

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
const SellingPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleGetStarted = () => {
    if (user?.userId) navigate(`/partneronboarding/${user.userId}`);
    else navigate("/authswitch");
  };

  const perks = [
    { icon: DollarSign, title: "Keep more of what you earn", body: "Low commission, no hidden fees — you set your own prices." },
    { icon: Rocket, title: "Launch in minutes", body: "List your first product and open your store the same day." },
    { icon: ShieldCheck, title: "Buyer & seller protection", body: "Verified buyers and built-in protections mean fewer headaches." },
    { icon: Package, title: "Sell across categories", body: "One storefront, unlimited categories — grow at your own pace." },
  ];

  return (
    <div className="sp-root" style={{ minHeight: "100vh" }}>
      <style>{CSS}</style>

      <div className="sp-wrap" style={{ maxWidth: 860, margin: "0 auto", padding: "56px 24px 72px" }}>
        {/* ── HERO ── */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div className="sp-hero-emoji" style={{ fontSize: 56, marginBottom: 14 }}>🛍️</div>
          <h1
            className="sp-hero-title"
            style={{
              fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 34,
              letterSpacing: "-0.02em", margin: "0 0 12px", color: HEART.dark,
            }}
          >
            Got something to sell?<br />Heartlas has a shelf for it.
          </h1>
          <p style={{ fontSize: 15, color: HEART.textMuted, lineHeight: 1.7, maxWidth: 480, margin: "0 auto 28px" }}>
            Open your storefront and start selling to shoppers across 100+
            countries — fashion, electronics, groceries, and just about
            anything else. It's free to start.
          </p>
          <button className="sp-cta" onClick={handleGetStarted}>
            <Store size={18} /> Start Selling <ArrowRight size={16} />
          </button>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 18 }}>
            <div style={{ display: "flex" }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={14} color={HEART.gold} fill={HEART.gold} strokeWidth={0} />
              ))}
            </div>
            <span style={{ fontSize: 12.5, color: HEART.textFaint, fontWeight: 600 }}>
              Trusted by sellers everywhere
            </span>
          </div>
        </div>

        {/* ── "SELL ANYTHING" CATEGORY PILLS ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 48 }}>
          {CATEGORIES.map((c) => (
            <span key={c} className="sp-cat-pill">{c}</span>
          ))}
        </div>

        {/* ── PERKS ── */}
        <div
          className="sp-perk-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 52 }}
        >
          {perks.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="sp-perk-card" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="sp-perk-icon">
                <Icon size={20} />
              </span>
              <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, margin: 0 }}>{title}</p>
              <p style={{ fontSize: 13, color: HEART.textMuted, lineHeight: 1.6, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div style={{ marginBottom: 44 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, justifyContent: "center" }}>
            <Sparkles size={16} color={HEART.red} />
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 20, margin: 0 }}>
              Questions, answered
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {FAQS.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={item.q} className="sp-faq-item">
                  <button className="sp-faq-q" onClick={() => setOpenFaq(open ? null : i)}>
                    {item.q}
                    <ChevronDown size={18} className={`sp-faq-chevron${open ? " open" : ""}`} />
                  </button>
                  <div className={`sp-faq-a${open ? " open" : ""}`}>{item.a}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── BOTTOM CTA ── */}
        <div
          style={{
            textAlign: "center", background: HEART.panelBg, border: `1.5px solid ${HEART.border}`,
            borderRadius: 20, padding: "32px 24px",
          }}
        >
          <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, margin: "0 0 8px" }}>
            Ready to open your store?
          </p>
          <p style={{ fontSize: 13.5, color: HEART.textMuted, margin: "0 0 20px" }}>
            It only takes a few minutes to list your first item.
          </p>
          <button className="sp-cta" onClick={handleGetStarted}>
            <Store size={18} /> Start Selling <ArrowRight size={16} />
          </button>
        </div>

        {/* ── FOOT LINKS (relative paths, no hardcoded domain) ── */}
        <div
          style={{
            display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap",
            marginTop: 40, paddingTop: 24, borderTop: `1px solid ${HEART.border}`,
          }}
        >
          <span className="sp-footlink" onClick={() => navigate("/pages/terms")}>Terms</span>
          <span className="sp-footlink" onClick={() => navigate("/pages/privacy")}>Privacy</span>
          <span className="sp-footlink" onClick={() => navigate("/pages/seller-guidelines")}>Seller Guidelines</span>
          <span className="sp-footlink" onClick={() => navigate("/support")}>Help Center</span>
        </div>
      </div>
    </div>
  );
};

export default SellingPage;
